import { useEffect, useState } from 'react';
import useSWR, { type Fetcher, unstable_serialize, useSWRConfig } from 'swr';

/**
 * Reads a record whose document links are presigned and may have expired while cached.
 * Nothing shows until a fetch that started after the record was opened has landed. From
 * then on the record stays on screen while SWR revalidates it, on refocus or after a
 * save, so a dialog open over it stays mounted.
 */
export function useRefetchedOnOpen<Data, Key extends readonly [string, string]>(
  key: Key | null,
  fetcher: Fetcher<Data, Key>,
) {
  const { cache } = useSWRConfig();
  // Opening the record fetches it below, so SWR only fetches by itself what isn't cached.
  const { data, error, isValidating, mutate } = useSWR(key, fetcher, {
    revalidateIfStale: false,
    dedupingInterval: 0,
  });
  const keyId = key ? unstable_serialize(key) : null;

  // `fetching`: a fetch was seen in flight since the record opened. `fresh`: one landed.
  const [opened, setOpened] = useState({ keyId, fetching: false, fresh: false });
  if (opened.keyId !== keyId) {
    setOpened({ keyId, fetching: false, fresh: false });
  } else if (!opened.fresh && isValidating && !opened.fetching) {
    setOpened({ ...opened, fetching: true });
  } else if (!opened.fresh && !isValidating && opened.fetching && !error) {
    setOpened({ ...opened, fresh: true });
  }

  // SWR shows cached data without fetching first, so fetch it. With nothing cached, SWR's
  // own fetch is already under way. A cached `isValidating` alone isn't trusted: SWR can
  // leave it set after discarding a request.
  useEffect(() => {
    if (!keyId) return;
    const state = cache.get(keyId);
    if (state?.data === undefined && state?.isValidating) return;
    let current = true;
    void mutate().then(() => {
      const after = cache.get(keyId);
      // Superseded by a newer request, or failed: the render-time check settles it.
      if (!current || after?.isValidating || after?.error !== undefined) return;
      setOpened((state) => (state.keyId === keyId ? { ...state, fresh: true } : state));
    });
    return () => {
      current = false;
    };
  }, [cache, keyId, mutate]);

  const fresh = opened.keyId === keyId && opened.fresh;
  // An error left from before the record opened says nothing about the fetch made now.
  const settled = fresh || (opened.keyId === keyId && opened.fetching && !isValidating);
  const currentError = settled ? error : undefined;
  return {
    data: fresh ? data : undefined,
    error: currentError,
    loading: keyId !== null && !fresh && !currentError,
  };
}
